/**
 * What is wrong with a vehicle's figures for the period, as far as it can be told from what
 * Geotab answered with.
 *
 * These are about the data being incomplete, not about the values looking odd — a fill-up that is
 * far larger than the measured consumption is a plausibility question and is flagged on its own
 * column instead.
 */
export type EnergyDataIssue =
  /** The request for this vehicle failed, so nothing about it is known. */
  | "requestFailed"
  /** The consumption counters reported nothing at all for the period. */
  | "noReadings"
  /** Only a single counter reading came back, which is a level and not a consumption. */
  | "singleReading"
  /** The vehicle drove outside the stretch its counters reported for. */
  | "partialCoverage"
  /** A counter reading was lower than the one before it. */
  | "counterReset"
  /** Consumption was measured but no trip distance came back, so there is no rate. */
  | "noDistance"
  /** A list came back exactly at its results limit, so records may have been cut off. */
  | "truncated";

/** `pending` is not a verdict: the vehicle is in the report but has not been read yet. */
export type EnergyDataStatus = "ok" | "partial" | "missing" | "failed" | "pending";

/** The issues that leave the figures unusable rather than merely incomplete. */
const MISSING_ISSUES = new Set<EnergyDataIssue>(["noReadings", "singleReading"]);

/**
 * Reduces a vehicle's issues to the one status the grid sorts and filters on.
 *
 * @param issues - The issues found for the vehicle.
 * @returns The worst of them.
 */
export function energyDataStatus(issues: EnergyDataIssue[]): EnergyDataStatus {
  if (issues.includes("requestFailed")) return "failed";
  if (issues.some(issue => MISSING_ISSUES.has(issue))) return "missing";
  return issues.length > 0 ? "partial" : "ok";
}

/** Worst first, so the default sort of the grid puts what needs looking at on top. */
const STATUS_ORDER: EnergyDataStatus[] = ["failed", "missing", "partial", "ok", "pending"];

/**
 * Sort weight for a status, so `failed` sorts ahead of `ok` rather than alphabetically behind it.
 *
 * @param status - The status to weigh.
 */
export function energyDataStatusOrder(status: EnergyDataStatus): number {
  return STATUS_ORDER.indexOf(status);
}
